import { useUIState } from '../../contexts/UIStateContext';

export default function ImportProgressBanner({ onRefresh }) {
  const { getKnowledgeImportProgress, setKnowledgeImportProgress } = useUIState();
  const progress = getKnowledgeImportProgress();

  if (!progress) return null;

  const handleDismiss = () => {
    setKnowledgeImportProgress(null);
    if (progress.status === 'complete' && onRefresh) {
      onRefresh();
    }
  };

  const getStatusIcon = () => {
    switch (progress.status) {
      case 'processing':
      case 'importing':
      case 'generating':
        return (
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'complete':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (progress.status) {
      case 'processing':
        return 'Discovering content themes...';
      case 'importing':
        return 'Structuring document...';
      case 'generating':
        // Fallback to stage-specific messages (ignore detailed message from task-gen-server)
        if (progress.stage) {
          switch (progress.stage) {
            case 'starting':
              return 'Starting import...';
            case 'validating':
              return 'Validating content safety...';
            case 'items_start':
            case 'items_processing':
              return 'Generating neural links for items...';
            case 'collections_start':
            case 'collections_processing':
              return 'Generating neural links for collections...';
            default:
              return 'Generating neural links...';
          }
        }
        return 'Generating neural links...';
      case 'complete':
        return 'Import complete!';
      case 'error':
        return `Import failed: ${progress.error || 'Unknown error'}`;
      default:
        return '';
    }
  };

  const getDisplayFileName = () => {
    if (!progress.fileName) return '';

    const maxLength = 40;
    const fileName = progress.fileName;

    // Get file extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;

    // If name is short enough, return as-is
    if (fileName.length <= maxLength) {
      return fileName;
    }

    // Ellipsize the name but keep the extension
    const maxNameLength = maxLength - extension.length - 3; // 3 for "..."
    const ellipsizedName = nameWithoutExt.substring(0, maxNameLength) + '...';

    return ellipsizedName + extension;
  };

  const getStatusColor = () => {
    switch (progress.status) {
      case 'processing':
      case 'importing':
      case 'generating':
        return 'from-blue-600 to-purple-600';
      case 'complete':
        return 'from-green-600 to-emerald-600';
      case 'error':
        return 'from-red-600 to-orange-600';
      default:
        return 'from-gray-600 to-gray-700';
    }
  };

  const isInProgress = ['processing', 'importing', 'generating'].includes(progress.status);
  const isPreProcessing = ['processing', 'importing'].includes(progress.status);
  const showProgressBar = isInProgress && !isPreProcessing;

  return (
    <div className="px-3 pt-3 flex-shrink-0">
      <div className={`relative bg-gradient-to-r ${getStatusColor()} rounded-lg p-3 shadow-lg border border-white/10 mb-3`} style={{ color: 'var(--color-text-on-accent)' }}>
              {/* Progress Bar Background - only show during 'generating' phase */}
        {showProgressBar && (
        <div className="absolute inset-0 bg-white/10 rounded-lg overflow-hidden">
          <div
            className="h-full bg-white/20 transition-all duration-1000 ease-out"
            style={{ width: `${progress.progress || 0}%` }}
          />
        </div>
      )}

      {/* Content */}
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Status Icon */}
          <div className="flex-shrink-0" style={{ color: 'var(--color-text-on-accent)' }}>
            {getStatusIcon()}
          </div>

          {/* Status Text */}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-xs sm:text-sm" style={{ color: 'var(--color-text-on-accent)' }}>
              {getStatusText()}
            </div>
            <div className="text-[10px] sm:text-xs mt-0.5 truncate" style={{ color: 'rgba(255, 255, 255, 0.8)' }} title={progress.fileName}>
              {getDisplayFileName()}
            </div>
          </div>

          {/* Progress Percentage - only show during 'generating' phase */}
          {showProgressBar && (
            <div className="font-bold text-xs sm:text-sm tabular-nums transition-all duration-500 flex-shrink-0" style={{ color: 'var(--color-text-on-accent)' }}>
              {progress.progress || 0}%
            </div>
          )}
        </div>

        {/* Dismiss Button */}
        {!isInProgress && (
          <button
            onClick={handleDismiss}
            className="ml-4 p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            style={{ color: 'rgba(255, 255, 255, 0.8)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'}
            title="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
    </div>
  );
}
